import React, {
  createContext, useContext, useEffect, useMemo, useRef, useState, useCallback,
} from "react";
import { api } from "../lib/api";
import { useLanguage } from "./LanguageContext";

/**
 * AutoTranslate — runtime UI translation.
 *
 * Every `<Tx>English string</Tx>` on any page is registered on mount. When
 * the active language changes to non-English, this provider batches the set
 * of unique English strings and calls `POST /api/i18n/translate` (MongoDB
 * cached, LLM fallback). Cached translations are also mirrored to
 * localStorage so repeat visits are instant.
 *
 * Why: manually adding hundreds of i18n keys across 10+ pages is brittle.
 * This provides full 5-language coverage with zero per-string configuration.
 */
const AutoTranslateContext = createContext(null);
const LS_PREFIX = "icc_ui_tr_v1_";

const loadLocal = (lang) => {
  try {
    const raw = localStorage.getItem(LS_PREFIX + lang);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
};
const saveLocal = (lang, map) => {
  try {
    localStorage.setItem(LS_PREFIX + lang, JSON.stringify(map));
  } catch (e) {
    // localStorage quota — ignore
  }
};

export const AutoTranslateProvider = ({ children }) => {
  const { lang } = useLanguage();
  // Every string currently mounted (Set of unique EN texts)
  const mountedRef = useRef(new Set());
  // Batched fetch queue
  const pendingRef = useRef(new Set());
  const timerRef = useRef(null);

  // Cached translations for the active language: { en_text: translated }
  const [cache, setCache] = useState(() => loadLocal(lang));
  // Reload cache when lang changes
  useEffect(() => {
    setCache(loadLocal(lang));
  }, [lang]);

  const flush = useCallback(async () => {
    if (!lang || lang === "en") {
      pendingRef.current.clear();
      return;
    }
    const local = loadLocal(lang);
    const missing = Array.from(pendingRef.current).filter((t) => !local[t]);
    pendingRef.current.clear();
    if (!missing.length) return;
    try {
      const r = await api.post("/i18n/translate", { texts: missing, lang });
      const translations = r?.data?.translations || {};
      const next = { ...local, ...translations };
      saveLocal(lang, next);
      setCache((prev) => ({ ...prev, ...translations }));
    } catch (e) {
      // Network / server failure — leave English visible.
    }
  }, [lang]);

  const enqueue = useCallback(
    (text) => {
      if (!text || typeof text !== "string" || !text.trim()) return;
      if (!lang || lang === "en") return;
      mountedRef.current.add(text);
      // Already known?
      if (cache[text]) return;
      pendingRef.current.add(text);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, 250);
    },
    [cache, lang, flush]
  );

  const translate = useCallback(
    (text) => {
      if (!lang || lang === "en") return text;
      return cache[text] || text;
    },
    [cache, lang]
  );

  const value = useMemo(() => ({ enqueue, translate, lang }), [enqueue, translate, lang]);

  return <AutoTranslateContext.Provider value={value}>{children}</AutoTranslateContext.Provider>;
};

const useAutoTranslate = () => useContext(AutoTranslateContext);

/**
 * <Tx>English text</Tx> — displays the child text translated to the active
 * language. Only accepts plain string children.
 */
export const Tx = ({ children }) => {
  const ctx = useAutoTranslate();
  const text = typeof children === "string" ? children : Array.isArray(children) ? children.join("") : "";
  useEffect(() => {
    if (ctx) ctx.enqueue(text);
  }, [ctx, text]);
  if (!ctx) return text;
  return ctx.translate(text);
};

/** useTx() — imperative hook for cases where you cannot render `<Tx>`
 *  (placeholders, aria-labels, alt text, etc.). */
export const useTx = () => {
  const ctx = useAutoTranslate();
  return useCallback(
    (text) => {
      if (!ctx || !text || typeof text !== "string") return text;
      ctx.enqueue(text);
      return ctx.translate(text);
    },
    [ctx]
  );
};

export default AutoTranslateContext;
